import React, { useState, useEffect } from 'react';
import { Form, Row } from 'react-bootstrap';
import { sortBy, cloneDeep } from 'lodash';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrashAlt, faSave, faClone } from '@fortawesome/pro-solid-svg-icons';

import ApiServiceServerless from '../../../xhr_libs';
import ConfirmationModal, { useConfirm } from '../../modals/ConfirmationModal';
import PlugTemplateRule from './PlugTemplateRule';
import AddPlugCustomTemplateRuleModal from '../../modals/PlugCustomTemplateModals/AddPlugCustomTemplateRuleModal';
import { DEFAULT_RULE } from './helpers';
import ClonePlugCustomTemplateRuleModal from '../../modals/PlugCustomTemplateModals/ClonePlugCustomTemplateRuleModal';

const getStyles = (disabled) => {
  return {
    iconStyle: {
      fontSize: '18px',
      margin: '0.25em 0.25em 0em 0.25em',
      verticalAlign: 'text-bottom',
      cursor: disabled ? 'none' : 'pointer',
      color: 'var(--et_tab_grey)',
      opacity: disabled ? '0.5' : '1.0',
    },
  };
};

export default function PlugCustomTemplateUsageRules(props) {
  const {
    activeTemplate,
    setTemplates,
    organizationId,
    showToast,
    isSaving,
    setIsSaving,
  } = props;

  const [rules, setRules] = useState([]);

  const [activeRuleId, setActiveRuleId] = useState(null);
  const [activeRule, setActiveRule] = useState(null);

  const [editRule, setEditRule] = useState(DEFAULT_RULE);

  const [clausesBoolen, setClausesBoolean] = useState(0);
  const [localClauses, setLocalClauses] = useState([]);
  const [clauseId, setClauseId] = useState(1);

  const [showAddModal, setShowAddModal] = useState(false);
  const [showCloneModal, setShowCloneModal] = useState(false);

  const [confirmationModalProps, withConfirm] = useConfirm();
  const [confirmText, setConfirmText] = useState('Confirm');
  const [submitVariant, setSubmitVariant] = useState('success');

  useEffect(() => {
    if (activeRuleId !== null) {
      let rule = rules.find((rule) => rule.id === activeRuleId);
      setActiveRule(rule ? rule : null);
    } else {
      setActiveRule(null);
    }
  }, [rules, activeRuleId]);

  useEffect(() => {
    if (activeTemplate) {
      setRules(
        activeTemplate.rules.filter((rule) => rule.rule_type === 'usage')
      );
    }
  }, [activeTemplate]);

  useEffect(() => {
    if (activeRule) {
      setEditRule(cloneDeep(activeRule));
    } else {
      setEditRule(DEFAULT_RULE);
    }
  }, [activeRule]);

  const updateRow = (key, value) => {
    setEditRule((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  useEffect(() => {
    if (activeRule) {
      const local_clauses = activeRule.clauses;
      setLocalClauses(local_clauses.clause_data);
      setClausesBoolean(local_clauses.clause_boolean);
      let temp_clauses_id = 1;
      local_clauses.clause_data.forEach((cl) => {
        if (cl.id > temp_clauses_id) {
          temp_clauses_id = cl.id;
        }
        if (cl.clause_type === 'group') {
          cl.clause_data.forEach((sub_cl) => {
            if (sub_cl.id > temp_clauses_id) {
              temp_clauses_id = sub_cl.id;
            }
          });
        }
      });
      setClauseId(temp_clauses_id + 1);
    }
  }, [activeRule]);

  const handlePayload = () => {
    let payload = {
      clauses: {
        clause_boolean: clausesBoolen,
        clause_data: localClauses,
      },
      name: editRule.name,
      rule_location: editRule.rule_location,
      rule_value: editRule.rule_value,
      operation: editRule.operation,
    };

    return payload;
  };

  const onDeleteClick = () => {
    setConfirmText('Delete');
    setSubmitVariant('danger');
    let msg = `Are you sure you want to delete this template? This cannot be undone.`;

    const beginSubscriptionDelete = () => {
      setIsSaving(true);
      handleDelete();
    };
    withConfirm(msg, beginSubscriptionDelete);
  };

  const handleDelete = () => {
    ApiServiceServerless.delete(`/custom_templates/rule/${activeRuleId}`, {
      authorization_id: organizationId,
    })
      .then((res) => {
        setActiveRuleId(null);
        setActiveRule(null);
        setTemplates((prev) =>
          prev.map((template) => {
            if (template.id !== activeTemplate.id) return template;
            return res.data;
          })
        );
        showToast('success', 'Success', `Rule was deleted.`);
      })
      .catch((error) => {
        showToast('danger', 'Error', error);
        throw error;
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  const saveTemplateRule = () => {
    setIsSaving(true);
    const payload = handlePayload();
    ApiServiceServerless.put(
      `/custom_templates/rule/${activeRule.id}`,
      payload,
      { authorization_id: organizationId }
    )
      .then((res) => {
        setTemplates((prev) =>
          prev.map((template) => {
            if (template.id !== activeTemplate.id) return template;
            let new_template = cloneDeep(template);
            new_template.rules = new_template.rules.map((rule) => {
              if (rule.id !== activeRule.id) return rule;
              let new_rule = res.data;
              new_rule.clause_data = rule.clause_data;
              return new_rule;
            });
            return new_template;
          })
        );
        showToast('success', 'Success', 'Rule Saved');
      })
      .catch((error) => {
        showToast('danger', 'Error', error);
        throw error;
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  return (
    <>
      <Row>
        <Form inline style={{ marginBottom: '0.5em' }}>
          <Form.Label
            style={{
              display: 'block',
              marginRight: '0.5em',
              marginLeft: '0.5em',
            }}
          >
            Rule:
          </Form.Label>
          <Form.Control
            style={{ float: 'right' }}
            as='select'
            size='sm'
            value={activeRuleId ? activeRuleId : ''}
            onChange={(e) => {
              let rid = e.target.value;
              setActiveRuleId(parseInt(rid));
              setActiveRule(null);
            }}
          >
            <option hidden value={'null'} key={'null-opt'}>
              Select a rule
            </option>
            {rules.length === 0 && (
              <option disabled value={'dis'} key={'null-dis'}>
                No Rules
              </option>
            )}
            {sortBy(rules, ['name']).map((rule) => (
              <option
                key={`rule-opt-${rule.id}-${rule.rule_type}`}
                value={rule.id}
              >
                {rule.name}
              </option>
            ))}
          </Form.Control>
        </Form>
        <FontAwesomeIcon
          style={{
            fontSize: '18px',
            margin: '0.25em',
            verticalAlign: 'text-bottom',
            cursor: 'pointer',
            color: 'var(--et_tab_grey)',
          }}
          icon={faPlus}
          title={'Add Rule'}
          disabled={isSaving}
          onClick={() => setShowAddModal(true)}
        />
        <FontAwesomeIcon
          style={{
            fontSize: '18px',
            margin: '0.25em',
            verticalAlign: 'text-bottom',
            cursor: 'pointer',
            color: 'var(--et_tab_grey)',
          }}
          icon={faClone}
          title={'Clone Rule'}
          disabled={isSaving}
          onClick={() => setShowCloneModal(true)}
        />
        {activeRuleId && (
          <>
            {' '}
            <FontAwesomeIcon
              style={getStyles(isSaving).iconStyle}
              title={'Save Data'}
              icon={faSave}
              disabled={isSaving}
              onClick={() => {
                if (!isSaving) {
                  saveTemplateRule();
                }
              }}
            />
            <FontAwesomeIcon
              style={getStyles(isSaving).iconStyle}
              icon={faTrashAlt}
              title='Delete Rule'
              disabled={isSaving}
              onClick={() => {
                if (!isSaving) {
                  onDeleteClick();
                }
              }}
            />
          </>
        )}
      </Row>
      {activeRuleId !== null && activeRule !== null && (
        <PlugTemplateRule
          ruleType={'usage'}
          activeTemplate={activeTemplate}
          activeRule={activeRule}
          editRule={editRule}
          updateRow={updateRow}
          clausesBoolen={clausesBoolen}
          setClausesBoolean={setClausesBoolean}
          localClauses={localClauses}
          setLocalClauses={setLocalClauses}
          clauseId={clauseId}
          setClauseId={setClauseId}
        />
      )}
      <AddPlugCustomTemplateRuleModal
        show={showAddModal}
        onHide={() => {
          setShowAddModal(false);
        }}
        showToast={showToast}
        isSaving={isSaving}
        setIsSaving={setIsSaving}
        setTemplates={setTemplates}
        setActiveRuleId={setActiveRuleId}
        ruleType={'usage'}
        activeTemplateId={activeTemplate.id}
        organizationId={organizationId}
      />
      <ClonePlugCustomTemplateRuleModal
        show={showCloneModal}
        onHide={() => {
          setShowCloneModal(false);
        }}
        showToast={showToast}
        isSaving={isSaving}
        setIsSaving={setIsSaving}
        rules={rules}
        setTemplates={setTemplates}
        setActiveRuleId={setActiveRuleId}
        ruleType={'usage'}
        activeTemplateId={activeTemplate.id}
        organizationId={organizationId}
      />
      <ConfirmationModal
        {...confirmationModalProps}
        confirmText={confirmText}
        submitVariant={submitVariant}
      />
    </>
  );
}
